import {
  EntityPlacementCtaTypeEnum,
  MonetizableEnum,
} from '@repo/supergraph-schemas/marketplace/enums';
import type { CtaType } from '@repo/supergraph-schemas/marketplace/types';
import type { RateTableEntity } from './drivers';

// Replicates the original rate table's CTA: @nerdwallet/react-banking-offer-card (simple) →
// @nerdwallet/react-marketplace-apply-now with hideInstitutionFromAnnotation → react-apply-now,
// using the @nerdwallet/marketplace selectors (NerdWallet-Archived/nwjs-marketplace).
// During the rehaul of the calculators we should consider standardizing these
// fields to what we do on other banking pages.

export const APPLY_TEXT = 'Learn More';
export const REVIEW_TEXT = 'Read review';

// selectors/common#isNonMonetizing: non-monetizing when `monetizable` is set to anything other
// than yes_assumed. (The legacy is_monetizing fallback isn't available here, so a missing value
// defaults to monetizing, as in the original.)
export function isNonMonetizing({ monetizable }: RateTableEntity) {
  return !!monetizable && monetizable !== MonetizableEnum.yesAssumed;
}

// react-apply-now#getAnnotationText: "on <issuer>'s website" (no phone number data here).
function getIssuerWebsiteText(issuer?: string) {
  if (!issuer) return undefined;
  const wordForWebsite = issuer.toLowerCase() === 'citibank' ? 'application' : 'website';
  return `on ${issuer}${/s$/.test(issuer) ? "'" : "'s"} ${wordForWebsite}`;
}

/**
 * Text under the "Learn More" button: the federally insured annotation ("Member FDIC",
 * "Federally insured by NCUA", "Deposits are FDIC Insured") when the institution has one,
 * otherwise "on <issuer>'s website". The issuer is the institution's display name.
 */
export function getApplyAnnotation({ federallyInsuredAnnotation, institution }: RateTableEntity) {
  return federallyInsuredAnnotation || getIssuerWebsiteText(institution?.displayName ?? undefined);
}

/**
 * The CTA to render, or undefined when the original would render nothing.
 * - Non-monetizing: "Read review" to the review page, with nothing under it.
 * - Otherwise: "Learn More" to the apply link, with the annotation under it.
 */
export function getRateTableCta(entity: RateTableEntity): CtaType | undefined {
  if (isNonMonetizing(entity)) {
    if (!entity.reviewLink) return undefined;
    return {
      link: entity.reviewLink,
      text: REVIEW_TEXT,
      type: EntityPlacementCtaTypeEnum.ReadReview,
      showIcon: false,
      subtext: undefined,
    };
  }

  if (!entity.cta?.link) return undefined;
  return {
    link: entity.cta.link,
    text: APPLY_TEXT,
    type: EntityPlacementCtaTypeEnum.ApplyNow,
    showIcon: true,
    subtext: getApplyAnnotation(entity),
  };
}
